import { RefObject, useCallback, useState } from "react";
import { Company } from "../../../setups/types/setupTypes";
import { Employee } from "../../types/processTypes";
import {
  buildAndSavePdfFromDom,
  buildBulkCvFileName,
} from "../utils/buildCvPdf";
import { waitForCvImagesReady } from "../utils/waitForCvImages";

export type BulkEmployeeProgress = {
  current: number;
  total: number;
};

/**
 * Downloads one PDF per employee by capturing the currently mounted CV preview
 * (same toCanvas path as PremiumTemplate / office bulk download).
 * Call `prepareEmployee` between iterations so the page can remount each CV.
 */
export function useBulkEmployeePdfDownload(
  previewRootRef: RefObject<HTMLElement | null>,
) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState<BulkEmployeeProgress>({
    current: 0,
    total: 0,
  });

  const downloadBulk = useCallback(
    async (
      employeeIds: string[],
      office: Company,
      prepareEmployee: (employeeId: string) => Promise<Employee | null>,
    ) => {
      if (!office.cvTemplate || employeeIds.length === 0) return;

      setIsDownloading(true);
      setProgress({ current: 0, total: employeeIds.length });

      try {
        for (let i = 0; i < employeeIds.length; i++) {
          setProgress({ current: i + 1, total: employeeIds.length });

          const employee = await prepareEmployee(employeeIds[i]);
          if (!employee?.givenName) continue;

          const previewRoot = previewRootRef.current;
          if (!previewRoot) continue;

          await waitForCvImagesReady(previewRoot, employee);

          await buildAndSavePdfFromDom(
            buildBulkCvFileName(employee, office),
            office.cvTemplate.numberOfPages ?? 1,
            previewRoot,
          );
        }
      } finally {
        setIsDownloading(false);
      }
    },
    [previewRootRef],
  );

  return { downloadBulk, isDownloading, progress };
}
